import { Box, Button } from '@mui/material';
import { styled } from '@mui/material/styles';
import { breakpoints } from '@/themes/themeOverrides/Breakpoints.js';
import { Form, useTranslate } from 'react-admin';
import { useProductBrands } from '@/composables/useProductBrands.js';
import { useCallback, useMemo, useState } from 'react';
import CustomCloseButton from '@/components/global/buttons/CustomCloseButton.jsx';
import DefinitionStep from '@/components/promotionalCampaigns/campaign/DefinitionStep.jsx';
import CriteriaStep from '@/components/promotionalCampaigns/campaign/CriteriaStep.jsx';
import DiscountStep from '@/components/promotionalCampaigns/campaign/DiscountStep.jsx';
import EmissionStep from '@/components/promotionalCampaigns/campaign/EmissionStep.jsx';
import SummaryStep from '@/components/promotionalCampaigns/campaign/SummaryStep.jsx';
import HeaderEditableTitle from '@/components/promotionalCampaigns/campaign/HeaderEditableTitle.jsx';
import ArrowLeftIcon from '@/assets/icons/arrow-left.svg?react';
import { campaignSteps } from '@/constants/CampaignSteps.jsx';
import SidebarSteps from '@/components/promotionalCampaigns/campaign/SidebarSteps.jsx';
import { Palette } from '@/themes/themeOverrides/Palette';
import { useCampaignStepCompletion } from '@/composables/useCampaignStepCompletion.js';

const StyledCampaignCreate = styled(Box)(() => ({
    '& .RaShow-noActions': {
        margin: '0',
    },
    '& .custom-dialog-right .popup-wrapper': {
        padding: '56px',
    },
    '& .popup-wrapper-top': {
        padding: '32px 40px !important',
        [`@media (max-width: ${breakpoints.values.md}px)`]: {
            padding: '32px 20px !important',
        },
        '& .popup-box--header': {
            padding: 0,
        },
    },
    '& .popup-box--header': {
        alignItems: 'center',
    },
    '& .padding-wrapper': {
        borderTop: `2px solid ${Palette.gray.gray75}`,
        padding: '40px',
        [`@media (max-width: ${breakpoints.values.md}px)`]: {
            padding: '24px 20px',
        },
    },
    '& .content-grid': {
        display: 'grid',
        gridTemplateColumns: '1fr 220px',
        gap: '56px',
        justifyContent: 'space-between',
        [`@media (max-width: ${breakpoints.values.lg}px)`]: {
            gridTemplateColumns: '1fr',
        },
    },
}));

export const CampaignCreate = ({ handleClose = () => {} }) => {
    const [isEditingTitle, setIsEditingTitle] = useState(false);
    const [activeStep, setActiveStep] = useState(0);

    const { data: brands = [] } = useProductBrands();
    const translate = useTranslate();

    const brandChoices = useMemo(() => brands.map((name, idx) => ({ id: idx + 1, name })), [brands]);

    const handleSubmit = useCallback(
        event => {
            event.preventDefault();
            event.stopPropagation();
            if (activeStep === campaignSteps.length - 1) {
                handleClose();
            }
            return false;
        },
        [handleClose, activeStep],
    );

    return (
        <StyledCampaignCreate>
            <Form
                onSubmit={handleSubmit}
                defaultValues={{
                    name: translate('resources.campaign.labels.newCampaignName'),
                    priority: 'normal',
                    promotionType: undefined,
                    productScope: undefined,
                    brandIds: [],
                    categoryIds: [],
                    productId: null,
                    productIds: [],
                    startDate: undefined,
                    endDate: undefined,
                    sponsored: false,
                    criteria: [],
                    discount: {
                        type: 'fixed',
                        fixedPercent: '',
                        depositTiers: [{ id: crypto.randomUUID(), limit: '', discount: '' }],
                        quantityTiers: [{ id: crypto.randomUUID(), limit: '', value: '' }],
                    },
                    imageDisplayDays: 7,
                    deviceDescription: '',
                }}
            >
                <Box className='popup-wrapper popup-wrapper-top'>
                    <Box className='popup-box popup-box--header'>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                            <HeaderEditableTitle isEditingTitle={isEditingTitle} onEdit={() => setIsEditingTitle(true)} />
                        </Box>
                        <Box>
                            <CustomCloseButton onClose={handleClose} />
                        </Box>
                    </Box>
                </Box>

                <CampaignCreateInner
                    activeStep={activeStep}
                    setActiveStep={setActiveStep}
                    brandChoices={brandChoices}
                    handleClose={handleClose}
                    stepLabels={['definition', 'criteria', 'discount', 'emission', 'summary'].map(key => translate(`campaign.steps.${key}`))}
                />
            </Form>
        </StyledCampaignCreate>
    );
};

const CampaignCreateInner = ({ activeStep, setActiveStep, brandChoices, handleClose, stepLabels = [] }) => {
    const { isStepCompleted, isStepEnabled, isCurrentCompleted, allStepsCompleted } = useCampaignStepCompletion(activeStep);
    const translate = useTranslate();

    const goNext = () => {
        if (!isCurrentCompleted) return;
        setActiveStep(activeStep + 1);
    };

    const goTo = index => {
        if (!isStepEnabled(index)) return;
        setActiveStep(index);
    };

    const handleSave = event => {
        event.preventDefault();
        event.stopPropagation();
        handleClose();
    };

    return (
        <Box className='popup-wrapper'>
            <Box className='content-grid'>
                <Box sx={{ maxWidth: '492px' }}>
                    {activeStep === 0 && <DefinitionStep brandChoices={brandChoices} />}
                    {activeStep === 1 && <CriteriaStep />}
                    {activeStep === 2 && <DiscountStep />}
                    {activeStep === 3 && <EmissionStep />}
                    {activeStep === 4 && <SummaryStep />}

                    <Box display='flex' justifyContent='space-between' mt={10}>
                        <Button
                            color='secondary'
                            type='button'
                            onClick={() => setActiveStep(prev => Math.max(prev - 1, 0))}
                            disabled={activeStep === 0}
                            sx={{
                                '& svg': {
                                    marginRight: 0,
                                },
                            }}
                            aria-label={translate('common.back')}
                        >
                            <ArrowLeftIcon />
                        </Button>
                        {activeStep < campaignSteps.length - 1 ? (
                            <Button variant='contained' type='button' onClick={goNext} disabled={!isCurrentCompleted}>
                                {translate('common.next')}
                            </Button>
                        ) : (
                            <Button variant='contained' type='button' onClick={handleSave}>
                                {translate('common.save')}
                            </Button>
                        )}
                    </Box>
                </Box>

                <SidebarSteps
                    steps={stepLabels}
                    activeIndex={activeStep}
                    onSelect={goTo}
                    isStepCompleted={isStepCompleted}
                    isStepEnabled={isStepEnabled}
                    allStepsCompleted={allStepsCompleted}
                />
            </Box>
        </Box>
    );
};
